(ns minorodata.components.transformations.db-copy
  (:require ["lucide-react" :refer [Plus X ChevronRight  Folder ListTree Table Box]]
            [applied-science.js-interop :as js-interop]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.components.spinner.core :as c.spinner]
            [mold-forms.core :as form-component]
            [mold-forms.model :refer [->form-model]]
            [re-frame.core :as re-frame]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [react :as react]
            [ui.alert :as alert]
            [ui.button :refer [button]]
            [ui.fields :refer [input select checkbox]]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]
            [ui.dialog :refer [dialog]]
            [minorodata.components.sql-console.sidebar :as c.sidebar]))

(defn max-if-sql-max-varchar-size
  "Print \"MAX\" if length is 65535 (redshift limit) or 16777216 (snoflake limit). Otherwise print length."
  [len]
  (if (or (= 65535 len)
          (= 16777216 len))
    "MAX"
    (str len)))

(defn title-sorting-fn [title-key]
  (fn [a-node b-node]
    (let [a-title (js-interop/get a-node title-key)
          b-title (js-interop/get b-node title-key)]
      (compare a-title b-title))))

;;
;; Tailwind + incremental fetch implementation starts here
;;

(defn get-offset [node-type]
  (case node-type
    "catalog" ""
    "schema"  "pl-2"
    "table"   "pl-4"
    "column"  "pl-6"
    0))

(defn get-item-offset [node-type]
  (case node-type
    "catalog" "sticky z-5 bg-kleene-background top-0"
    "schema"  "sticky z-4 bg-kleene-background top-6"
    "table"   "sticky z-3 bg-kleene-background top-12"
    "column"  "sticky z-2 bg-kleene-background top-16"
    0))


(defn node-type [node]
  (cond
    (:column-name node) "column"
    (:table-name node)  "table"
    (:table-schem node) "schema"
    (:table-cat node)   "catalog"
    :else               nil))

(defn type-icon [item-type]
  (case item-type
    "catalog" Folder
    "schema"  ListTree
    "table"   Table
    "column"  Box
    nil))

(defn node-object [node]
  (cond
    (:table-name node)  "columns"
    (:table-schem node) "tables"
    (:table-cat node)   "schemas"
    :else nil))

(defn node-title [node]
  (cond
    (:column-name node) (:column-name node)
    (:table-name node)  (:table-name node)
    (:table-schem node) (:table-schem node)
    (:table-cat node)   (:table-cat node)
    :else nil))

(defn fetch-params [node]
  (cond
    (:column-name node) nil
    (:table-name node)  {:object "columns"
                         :catalog (or (:table-catalog node) (:table-cat node))
                         :schema  (:table-schem node)
                         :table   (:table-name node)}
    (:table-schem node) {:object "tables"
                         :catalog (or (:table-catalog node) (:table-cat node))
                         :schema (:table-schem node)}
    (:table-cat node)   {:object "schemas"
                         :catalog (:table-cat node)}))

(defn generate-table-name [node]
  (str (:table-cat node) "." (:table-schem node) "." (:table-name node)))

;; Generates table name taking catalog into account. Necessary for regex
;; generation
(defn generate-table-name-2 [node]
  (str (or (:table-cat node) (:table-catalog node)) "." (:table-schem node) "." (:table-name node)))


(defn table-name->regex [table-name]
  (let [table-name-with-wildcards (-> table-name
                                      (clojure.string/replace #"\.\.$" "\\..*\\..*")
                                      (clojure.string/replace #"\.$" "\\..*"))]
    (re-pattern table-name-with-wildcards)))


(defn check-selected-nested-nodes [node selected-tables]
  (let [table-name (generate-table-name-2 node)
        pattern    (table-name->regex table-name)
        candidates (map #(re-matches pattern %)
                        selected-tables)
        check?     (some some? candidates)]
    check?))


(defn generic-tree-node []
  (let [open? (reagent/atom false)]
    (fn [{:keys [icon title subtitle children on-click exandable? class item-class node child-nodes]}]
      (let [type (node-type node)
            table-name (generate-table-name node)
            selected-tables @(re-posh/subscribe [:environments.subs/tables-to-copy])
            has-selected-nested-nodes? (check-selected-nested-nodes node selected-tables)]
        [:div {:class (clsx "" class)}
         [:div {:class (clsx "flex items-center py-px pr-1 transition-colors cursor-pointer hover:bg-kleene-neutral-04 dark:hover:bg-kleene-dark-02 rounded-xs"
                             item-class)
                :on-click (fn []
                            (when on-click (on-click (not @open?)))
                            (when exandable? (swap! open? not)))}

          [:header {:class "flex justify-between items-center w-full overflow-hidden"}
           [:div {:class "flex items-center overflow-hidden"}
            (if-not exandable?
              [:span {:class "inline-block h-6 w-6 shrink-0"}]
              [:> ChevronRight {:class (clsx "leading-zero align-top text-kleene-icon shrink-0" (when @open? "rotate-90")) :size 20}])

            [:> icon
             {:size 15 :class "text-kleene-icon opacity-90 ml-1 mr-2 shrink-0 leading-zero align-top"}]
            [:span {:class "whitespace-nowrap overflow-hidden text-ellipsis text-xs"} title]]

           (if (and (= "schema" type)
                    (seq child-nodes))
             (let [nested-tables  (set (map generate-table-name child-nodes))
                   all-selected?  (clojure.set/subset? nested-tables selected-tables)
                   some-selected? (seq (clojure.set/intersection nested-tables selected-tables))]
               [:span {:on-click (fn [e] (.stopPropagation e))}
                [checkbox {:checked   (cond
                                        all-selected? true
                                        some-selected? "indeterminate"
                                        :else false)
                           :on-change (fn [changed-value]
                                        (if changed-value
                                          (dispatch [:environments.events/add-table-to-copy nested-tables])
                                          (dispatch [:environments.events/remove-table-to-copy nested-tables])))}]])
             (when (and (not (= "table" type))
                        has-selected-nested-nodes?)
               [:span {:class "rounded-full h-3 w-3 bg-kleene-text"
                       :title "Has some of the nested nodes selected"}]))

           (when (= "table" type)
             [checkbox {:checked   (contains? selected-tables table-name)
                        :on-change (fn [changed-value]
                                     (if changed-value
                                       (dispatch [:environments.events/add-table-to-copy table-name])
                                       (dispatch [:environments.events/remove-table-to-copy table-name])))}])]]

         (when @open? children)]))))

(defn node-column-type-name [node]
  (let [type-name (:type-name node)
        char-octet-length (:char-octet-length node)]
    (if (and (= "VARCHAR" type-name)
             (some? char-octet-length))
      (str type-name " (" (max-if-sql-max-varchar-size char-octet-length) ")")
      type-name)))

(defn node-description [node children-count]
  (if (:column-name node)
    (node-column-type-name node)
    (when children-count
      (str children-count " " (node-object node)))))

(defn select-table [selected-table])

(defn tree-node [{:keys [node on-click]}]
  (let [type            (node-type node)
        title           (node-title node)
        params          (fetch-params node)
        children        (when (not= type "table")
                          @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params params]
                                               :sql-metadata-partial/metadata]))
        children-count (some-> children count)]

    [generic-tree-node
     {:icon       (type-icon type)
      :title      title
      :subtitle   (node-description node children-count)
      :on-click   (fn [v]
                    (if (= type "table")
                      (select-table node)
                      (when (and v params) (on-click v params))))
      :exandable? (not= type "table")
      :class      (get-offset type)
      :item-class (get-item-offset type)

      :node        node
      :child-nodes children
      :children
      [:<>
       (for [s children]
         ^{:key (str (:table-cat s) (:table-schem s) (:table-name s) (:column-name s))}
         [tree-node {:on-click on-click
                     :params   params
                     :node     s}])]}]))

(defn tree-view [{:keys [children]}]
  (let [fetch-catalog-tables #(dispatch [:sql-console.api/fetch-metadata %])]
    (into
     [:div]
     (for [i (range (count children))]
       [tree-node {:on-click (fn [opened params]
                               (when opened (fetch-catalog-tables params)))
                   :node     (nth children i)}]))))


(defn modal []
  (let [tables (reagent/atom [])
        handle-envs? @(re-posh/subscribe [:unleash/is-enabled "fe.environments"])]
    (fn []

      (let [opened?  @(re-posh/subscribe [:sql-console-modal.subs/db-copy-modal-open?])

            env-id @(re-posh/subscribe [:sql-console-modal.subs/current-environment-id])
            env    (when env-id
                     @(re-posh/subscribe [:environment/pull [:environment/environment-id env-id]]))

            all-tables     @(re-posh/subscribe [:environments.subs/all-input-tables])
            initial-tables @(re-posh/subscribe [:environments.subs/initial-input-tables])

            env-name (:environment/name env)

            hide-modal-fn (fn hide-modal []
                            (dispatch [:environments.events/clear-table-to-copy])
                            (dispatch [:sql-console-modal.events/toggle-db-copy-modal false]))

            catalogs @(re-posh/subscribe [:sql-metadata-partial/prop [:sql-metadata-partial/params {:object "catalogs"}]
                                          :sql-metadata-partial/metadata])
            catalogs (sort (title-sorting-fn :table-cat) catalogs)

            selected-tables @(re-posh/subscribe [:environments.subs/tables-to-copy])

            loading? @(re-posh/subscribe [:mold-http/loading? :environments.api/copy-tables])]

        (react/useEffect
         (fn []
           (when handle-envs?
             (dispatch [:environments.api/fetch-all-input-tables])
             (dispatch [:environments.api/fetch-initial-input-tables]))
           (fn []))
         #js [])

        [dialog {:open           (boolean opened?)
                 :root-props     {}
                 :content-props  {:size :medium}
                 :title          (str "Copy data to " env-name)
                 :on-open-change hide-modal-fn
                 :content [:div {:class "p-6 pb-2"}
                           [:div {:class "flex flex-row gap-4"}
                            [:div {:class "w-1/2"}
                             [:p
                              env-name
                              " doesn't process your production data, so you can copy data into the environment here"]

                             [:div {:class ["flex flex-row items-center my-3 p-4 pr-2"
                                            "cursor-pointer"
                                            "hover:bg-kleene-neutral-04 dark:hover:bg-kleene-dark-02"
                                            "border border-kleene-neutral-02 dark:border-kleene-dark-02 rounded-lg"]
                                    :on-click (fn []
                                                (dispatch [:environments.events/add-table-to-copy all-tables]))}
                              [:div {}
                               [typography/h3 "Select all transform input tables"]
                               [:p {:class "text-kleene-dark-05"} "Copies every table as an input of any transform. This transforms the most data."]]
                              [:div {}
                               [:> ChevronRight]]]



                             [:div {:class ["flex flex-row items-center my-3 p-4 pr-2"
                                            "cursor-pointer"
                                            "hover:bg-kleene-neutral-04 dark:hover:bg-kleene-dark-02"
                                            "border border-kleene-neutral-02 dark:border-kleene-dark-02 rounded-lg"]
                                    :on-click (fn []
                                                (dispatch [:environments.events/add-table-to-copy initial-tables]))}
                              [:div {}
                               [typography/h3 "Select input tables of first transforms"]
                               [:p {:class "text-kleene-dark-05"} "Copies every table identified as an input table of any transform with no upstream dependencies. Less data."]]
                              [:div {}
                               [:> ChevronRight]]]

                             [:p "Notes"]
                             [:ul {:class "list-disc pl-6"}
                              [:li "You cannot delete tables in " env-name " here"]
                              [:li "A table will be overwritten if it exists in " env-name]
                              [:li "You can find guidance in the docs on how to copy a subset of data using SQL"]
                              [:li "The time taken to copy varies, depending on the number of tables and the warehouse. But can be up to 30 mins"]]]

                            [:div {:class "w-1/2"}

                             [:div {:class "flex flex-row mt-2"}
                              [typography/label {:class "grow"} "Database"]

                              (when (< 0 (count selected-tables))
                                [typography/small (str (count selected-tables) " selected")])]

                             [:div {:class ["max-h-[450px] overflow-scroll"
                                            "border border-kleene-neutral-02 dark:border-kleene-dark-02 rounded-lg"]}
                              [tree-view {:children catalogs}]]


                             [:div {:class "text-right pt-4"}
                              [button {:size       :small
                                       :variant    :secondary
                                       :on-click hide-modal-fn}
                               "Cancel"]
                              [button {:size       :small
                                       :class "ml-4"
                                       :variant    :primary
                                       :loading    loading?
                                       :on-click (fn []
                                                   (dispatch [:environments.api/copy-tables {:tables         selected-tables
                                                                                             :environment-id env-id}]))}
                               "Copy these tables"]]]]]}]))))
